import { JobType } from 'interfaces/job';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { getStainingMethodByStainType, StainingMethod, StainType } from 'interfaces/stainType';
import { compact, filter, find, flatMap, groupBy, map, orderBy, size, some, uniq, values } from 'lodash';
import { getEffectiveChannelModality, isRnaChannel } from 'utils/multiplex/channelModality';

export interface StudyWorkflowInventoryCount {
  label: string;
  count: number;
}

export interface StudyWorkflowInventory {
  caseSlideCount: number;
  ungroupedSlideCount: number;
  slideRecordCount: number;
  slidesWithStainingMetadataCount: number;
  hasHeOrIhcSlides: boolean;
  modalityCounts: StudyWorkflowInventoryCount[];
  caseCompositionCounts: StudyWorkflowInventoryCount[];
}

export interface StudyWorkflowStepMetric {
  label: string;
  value: number;
}

export const CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE = 'Loading cases and slide assignments…';

export const getCaseOrganizationWorkflowMetrics = (
  inventory: StudyWorkflowInventory,
  researchCaseCount: number
): StudyWorkflowStepMetric[] => [
  { label: 'Slides assigned to cases', value: inventory.caseSlideCount },
  { label: 'Slides awaiting case assignment', value: inventory.ungroupedSlideCount },
  ...(researchCaseCount ? [{ label: 'Research cases', value: researchCaseCount }] : []),
];

export interface StudyWorkflowMarkerGroup {
  label: string;
  markers: StainType[];
}

export interface StudyWorkflowImageAnalysisSlideScopes {
  allSlideIds: string[];
  brightfieldSlideIds: string[];
  multiplexSlideIds: string[];
  tissueSegmentationScopes: Array<{
    id: string;
    modalityLabel: string;
    label: string;
    slideIds: string[];
    jobType: JobType;
  }>;
}

const stainingMethodLabels: Record<StainingMethod, string> = {
  [StainingMethod.he]: 'H&E',
  [StainingMethod.ihc]: 'IHC',
  [StainingMethod.chromogenicMplex]: 'Chromogenic multiplex',
  [StainingMethod.mplex]: 'Multiplex',
};

const markerNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const getNaturalMarkerSortValue = (marker: StainType) => marker.displayName.replace(/[^a-z0-9]/gi, '');

const countLabels = (labels: string[]): StudyWorkflowInventoryCount[] => {
  const counts = labels.reduce<Record<string, number>>((currentCounts, label) => {
    currentCounts[label] = (currentCounts[label] || 0) + 1;
    return currentCounts;
  }, {});

  return orderBy(
    map(counts, (count, label) => ({ label, count })),
    ['count', 'label'],
    ['desc', 'asc']
  );
};

export const getStudyWorkflowImageAnalysisSlideScopes = (
  procedures: Procedure[]
): StudyWorkflowImageAnalysisSlideScopes => {
  const slides = flatMap(procedures, (procedure) => procedure.slides || []);
  const allSlideIds = uniq(map(filter(slides, 'stainingType'), 'id'));
  const slidesByStainingMethod = groupBy(filter(slides, 'stainingType'), (slide) =>
    getStainingMethodByStainType(slide.stainingType as string)
  );
  const multiplexSlideIds = uniq(map(slidesByStainingMethod[StainingMethod.mplex] || [], 'id'));
  const getTissueSegmentationScope = (stainingMethod: StainingMethod, jobType: JobType) => ({
    id: `${stainingMethod}-tissue-segmentation`,
    modalityLabel: stainingMethodLabels[stainingMethod],
    label: 'Tissue segmentation',
    slideIds: uniq(map(slidesByStainingMethod[stainingMethod] || [], 'id')),
    jobType,
  });

  return {
    allSlideIds,
    brightfieldSlideIds: filter(allSlideIds, (slideId) => !multiplexSlideIds.includes(slideId)),
    multiplexSlideIds,
    tissueSegmentationScopes: [
      getTissueSegmentationScope(StainingMethod.he, JobType.BrightfieldTissueSegmentationMask),
      getTissueSegmentationScope(StainingMethod.ihc, JobType.BrightfieldTissueSegmentationMask),
      getTissueSegmentationScope(StainingMethod.chromogenicMplex, JobType.BrightfieldTissueSegmentationMask),
      getTissueSegmentationScope(StainingMethod.mplex, JobType.MultiplexTissueSegmentationMask),
    ],
  };
};

export const getStudyWorkflowMarkerGroups = (
  assignedMarkerTypes: StainType[],
  stainTypeOptions: StainType[],
  channelModalities?: Record<string, string>
): StudyWorkflowMarkerGroup[] => {
  const markersWithMetadata = map(assignedMarkerTypes, (markerType) => ({
    ...markerType,
    ...find(stainTypeOptions, { id: markerType.id }),
    displayName: markerType.displayName,
  }));
  const getSortedMarkers = (markers: StainType[]) =>
    [...markers].sort((leftMarker, rightMarker) => {
      const normalizedComparison = markerNameCollator.compare(
        getNaturalMarkerSortValue(leftMarker),
        getNaturalMarkerSortValue(rightMarker)
      );

      return normalizedComparison || markerNameCollator.compare(leftMarker.displayName, rightMarker.displayName);
    });
  const getModality = (marker: StainType) =>
    getEffectiveChannelModality({ markerType: marker.id, stainTypeOptions, channelModalities });
  const proteinMarkers = filter(markersWithMetadata, (marker) => getModality(marker) === 'mif');
  const groups: StudyWorkflowMarkerGroup[] = [
    {
      label: 'DNA markers',
      markers: getSortedMarkers(filter(markersWithMetadata, (marker) => getModality(marker) === 'dna')),
    },
    {
      label: 'RNA probes',
      markers: getSortedMarkers(filter(markersWithMetadata, (marker) => getModality(marker) === 'rna')),
    },
    { label: 'Lineage markers', markers: getSortedMarkers(filter(proteinMarkers, 'lineage')) },
    { label: 'Phenotypic markers', markers: getSortedMarkers(filter(proteinMarkers, 'phenotypic')) },
    {
      label: 'Other assigned markers',
      markers: getSortedMarkers(filter(proteinMarkers, (marker) => !marker.lineage && !marker.phenotypic)),
    },
  ];

  return filter(groups, ({ markers }) => markers.length > 0);
};

export const getStudyWorkflowInventory = (
  procedures: Procedure[],
  ungroupedSlides: Slide[] = []
): StudyWorkflowInventory => {
  const assignedSlides = flatMap(procedures, (procedure) => procedure.slides || []);
  const slides = [...assignedSlides, ...ungroupedSlides];

  const getSlideModality = (slide: (typeof slides)[number]) => {
    if (!slide.stainingType) {
      return undefined;
    }

    return stainingMethodLabels[getStainingMethodByStainType(slide.stainingType)];
  };
  const hasRnaProbes = (slide: (typeof slides)[number]) => some(values(slide.channels || {}), isRnaChannel);

  const modalityCounts = countLabels(compact(map(slides, getSlideModality)));
  const caseCompositionCounts = countLabels(
    compact(
      map(procedures, (procedure) => {
        const caseSlides = procedure.slides || [];
        const caseHasRnaProbes = some(caseSlides, hasRnaProbes);
        const caseModalities = uniq(compact(map(caseSlides, getSlideModality)))
          .sort()
          .map((modality) =>
            modality === stainingMethodLabels[StainingMethod.mplex] && caseHasRnaProbes
              ? `${modality} (includes RNA probes)`
              : modality
          );
        return caseModalities.length ? caseModalities.join(' + ') : undefined;
      })
    )
  );

  return {
    caseSlideCount: size(uniq(map(assignedSlides, 'id'))),
    ungroupedSlideCount: size(ungroupedSlides),
    slideRecordCount: size(slides),
    slidesWithStainingMetadataCount: size(slides.filter((slide) => Boolean(slide.stainingType))),
    hasHeOrIhcSlides: slides.some(
      (slide) =>
        Boolean(slide.stainingType) &&
        [StainingMethod.he, StainingMethod.ihc].includes(getStainingMethodByStainType(slide.stainingType))
    ),
    modalityCounts,
    caseCompositionCounts,
  };
};
